import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { URL_KINDS } from '../../../shared/constants/url-kinds';
import { authoritiesAPI } from '../api/authorities';

const SOURCE_KINDS = ['website', 'calendar', 'registration', 'voter_id', 'absentee', 'polling'];

function SourceRow({ kind, url, isPending, onEdit, onSave, error }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url ?? '');
  const [localError, setLocalError] = useState('');

  const metadata = URL_KINDS[kind];

  const handleSave = () => {
    if (draft) {
      try {
        new URL(draft);
      } catch {
        setLocalError('Invalid URL (include https://)');
        return;
      }
    }
    onSave(draft);
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <input
          type="url"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setLocalError('');
          }}
          placeholder={`https://example.gov`}
          autoFocus
          className="w-full px-3 py-1.5 text-base border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {(localError || error) && <p className="text-xs text-red-500">{localError || error}</p>}
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setLocalError('');
            }}
            className="px-3 py-1 text-xs bg-neutral-100 text-neutral-600 rounded-lg hover:bg-neutral-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-neutral-100 py-4 last:border-b-0">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-medium text-neutral-900">{metadata.label}</h4>
        </div>
        <button
          onClick={() => {
            setDraft(url ?? '');
            setEditing(true);
          }}
          className="text-xs px-2 py-0.5 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-neutral-500 shrink-0"
        >
          Edit
        </button>
      </div>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-600 hover:text-primary-700 break-all"
        >
          {url}
        </a>
      ) : (
        <span className="text-xs text-neutral-400 italic">None set</span>
      )}
    </div>
  );
}

export default function AdminOfficialSources({ authority, authorityId, qc }) {
  const [editing, setEditing] = useState(null);

  const mutations = Object.fromEntries(
    SOURCE_KINDS.map((kind) => [
      kind,
      useMutation({
        mutationFn: (url) => authoritiesAPI.saveSource(authorityId, kind, url),
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ['authority-detail', authorityId] });
          setEditing(null);
        },
      }),
    ])
  );

  const getUrl = (kind) => {
    if (kind === 'website') return authority.websiteUrl;
    if (kind === 'calendar') return authority.calendarUrl;
    // For DataSource kinds, we'd need to look in the dataSources array if populated
    // For now, just return null
    return null;
  };

  return (
    <div className="space-y-4">
      {SOURCE_KINDS.map((kind) => {
        const mutation = mutations[kind];
        const url = getUrl(kind);
        return (
          <SourceRow
            key={kind}
            kind={kind}
            url={url}
            isPending={mutation.isPending}
            onSave={(newUrl) => mutation.mutate(newUrl)}
            error={mutation.error?.response?.data?.message}
          />
        );
      })}
    </div>
  );
}
